/**
 * RSA加密工具类 - 确保与Android加密结果一致
 */
class RSAEncryptionUtils {
  constructor() {
    this.RSA_ALGORITHM = 'RSA-OAEP';
    // 修改为SHA-1以匹配Android
    this.HASH_ALGORITHM = 'SHA-256';
  }

  /**
   * 导入公钥
   */
  async importPublicKey(publicKeyBase64) {
    try {
      if (!publicKeyBase64) {
        throw new Error('公钥为空');
      }

      // 清理可能的空白字符
      let cleanedKey = publicKeyBase64.trim();

      // Base64解码
      const binaryDer = this.base64ToArrayBuffer(cleanedKey);

      // 导入公钥，明确指定SHA-1
      return await window.crypto.subtle.importKey(
        'spki',
        binaryDer,
        {
          name: this.RSA_ALGORITHM,
          hash: { name: this.HASH_ALGORITHM } // 使用SHA-1
        },
        false,
        ['encrypt']
      );
    } catch (error) {
      throw new Error('公钥导入失败: ' + error.message);
    }
  }

  /**
   * 使用公钥加密数据（与Android保持一致）
   */
  async encryptWithPublicKey(data, publicKeyBase64) {
    try {
      if (!data || !publicKeyBase64) {
        throw new Error('加密数据或公钥为空');
      }

      // 导入公钥
      const publicKey = await this.importPublicKey(publicKeyBase64);

      // 使用UTF-8编码数据
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);

      // 执行加密
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        { 
          name: this.RSA_ALGORITHM
        },
        publicKey,
        dataBuffer
      );

      // 转换为Base64字符串（确保编码一致）
      return this.arrayBufferToBase64(encryptedBuffer);
    } catch (error) {
      this.console.error('RSA加密失败:', error);
      throw new Error('加密失败: ' + error.message);
    }
  }

  /**
   * 验证公钥格式
   */
  async validatePublicKey(publicKeyBase64) {
    try {
      if (!publicKeyBase64 || typeof publicKeyBase64 !== 'string') {
        return false;
      }

      // 基本格式检查
      const cleanedKey = publicKeyBase64.trim();
      if (cleanedKey.length < 50) {
        return false;
      }

      // 验证Base64格式
      if (!this.isValidBase64(cleanedKey)) {
        return false;
      }

      // 尝试导入公钥
      await this.importPublicKey(cleanedKey);
      return true;
    } catch (error) {
      this.console.error('公钥验证失败:', error);
      return false;
    }
  }

  /**
   * 工具方法：Base64字符串转ArrayBuffer
   */
  base64ToArrayBuffer(base64) {
    try {
      // 清理可能的URL安全字符
      let cleanedBase64 = base64.trim();
      
      // 确保是标准Base64
      cleanedBase64 = cleanedBase64.replace(/-/g, '+').replace(/_/g, '/');
      
      // 添加padding如果必要
      while (cleanedBase64.length % 4 !== 0) {
        cleanedBase64 += '=';
      }
      
      const binaryString = window.atob(cleanedBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    } catch (error) {
      throw new Error('Base64解码失败: ' + error.message);
    }
  }

  /**
   * 工具方法：ArrayBuffer转Base64字符串
   */
  arrayBufferToBase64(buffer) {
    try {
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      // 使用标准Base64编码
      return window.btoa(binary);
    } catch (error) {
      throw new Error('Base64编码失败: ' + error.message);
    }
  }

  /**
   * 检查Base64格式
   */
  isValidBase64(str) {
    try {
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(str)) {
        return false;
      }
      window.atob(str);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 测试加密功能
   */
  async testEncryption(publicKeyBase64) {
    try {
      const testData = 'test_message_' + Date.now();
      const encrypted = await this.encryptWithPublicKey(testData, publicKeyBase64);
      
      this.console.log('加密测试结果:', {
        original: testData,
        encrypted: encrypted,
        length: encrypted.length
      });
      
      return !!encrypted && encrypted.length > 0;
    } catch (error) {
      this.console.error('加密测试失败:', error);
      return false;
    }
  }
}

// 创建单例实例
const rsaEncryptionUtils = new RSAEncryptionUtils();

export default rsaEncryptionUtils;



// /**
//  * RSA加密工具类 - 使用RSA/ECB/PKCS1Padding模式
//  */
// class RSAEncryptionUtils {
//   constructor() {
//     this.RSA_ALGORITHM = 'RSAES-PKCS1-v1_5'; // 修改为PKCS1-v1_5
//   }

//   /**
//    * 导入公钥
//    */
//   async importPublicKey(publicKeyBase64) {
//     try {
//       if (!publicKeyBase64) {
//         throw new Error('公钥为空');
//       }

//       // 清理可能的空白字符
//       let cleanedKey = publicKeyBase64.trim();

//       // Base64解码
//       const binaryDer = this.base64ToArrayBuffer(cleanedKey);

//       // 导入公钥，使用PKCS1-v1_5
//       return await window.crypto.subtle.importKey(
//         'spki',
//         binaryDer,
//         {
//           name: this.RSA_ALGORITHM, // 使用RSAES-PKCS1-v1_5
//           // 注意：PKCS1-v1_5不需要指定hash参数
//         },
//         false,
//         ['encrypt']
//       );
//     } catch (error) {
//       throw new Error('公钥导入失败: ' + error.message);
//     }
//   }

//   /**
//    * 使用公钥加密数据
//    */
//   async encryptWithPublicKey(data, publicKeyBase64) {
//     try {
//       if (!data || !publicKeyBase64) {
//         throw new Error('加密数据或公钥为空');
//       }

//       // 导入公钥
//       const publicKey = await this.importPublicKey(publicKeyBase64);

//       // 使用UTF-8编码数据
//       const encoder = new TextEncoder();
//       const dataBuffer = encoder.encode(data);

//       // 执行加密（PKCS1-v1_5模式）
//       const encryptedBuffer = await window.crypto.subtle.encrypt(
//         { 
//           name: this.RSA_ALGORITHM // RSAES-PKCS1-v1_5
//         },
//         publicKey,
//         dataBuffer
//       );

//       // 转换为Base64字符串
//       return this.arrayBufferToBase64(encryptedBuffer);
//     } catch (error) {
//       this.console.error('RSA加密失败:', error);
//       throw new Error('加密失败: ' + error.message);
//     }
//   }

//   /**
//    * 验证公钥格式
//    */
//   async validatePublicKey(publicKeyBase64) {
//     try {
//       if (!publicKeyBase64 || typeof publicKeyBase64 !== 'string') {
//         return false;
//       }

//       // 基本格式检查
//       const cleanedKey = publicKeyBase64.trim();
//       if (cleanedKey.length < 50) {
//         return false;
//       }

//       // 验证Base64格式
//       if (!this.isValidBase64(cleanedKey)) {
//         return false;
//       }

//       // 尝试导入公钥
//       await this.importPublicKey(cleanedKey);
//       return true;
//     } catch (error) {
//       this.console.error('公钥验证失败:', error);
//       return false;
//     }
//   }

//   // 其他工具方法保持不变...
//   base64ToArrayBuffer(base64) {
//     try {
//       let cleanedBase64 = base64.trim();
//       cleanedBase64 = cleanedBase64.replace(/-/g, '+').replace(/_/g, '/');
//       while (cleanedBase64.length % 4 !== 0) {
//         cleanedBase64 += '=';
//       }
//       const binaryString = window.atob(cleanedBase64);
//       const bytes = new Uint8Array(binaryString.length);
//       for (let i = 0; i < binaryString.length; i++) {
//         bytes[i] = binaryString.charCodeAt(i);
//       }
//       return bytes.buffer;
//     } catch (error) {
//       throw new Error('Base64解码失败: ' + error.message);
//     }
//   }

//   arrayBufferToBase64(buffer) {
//     try {
//       const bytes = new Uint8Array(buffer);
//       let binary = '';
//       for (let i = 0; i < bytes.byteLength; i++) {
//         binary += String.fromCharCode(bytes[i]);
//       }
//       return window.btoa(binary);
//     } catch (error) {
//       throw new Error('Base64编码失败: ' + error.message);
//     }
//   }

//   isValidBase64(str) {
//     try {
//       const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
//       if (!base64Regex.test(str)) {
//         return false;
//       }
//       window.atob(str);
//       return true;
//     } catch (error) {
//       return false;
//     }
//   }
// }

// const rsaEncryptionUtils = new RSAEncryptionUtils();
// export default rsaEncryptionUtils;